import React from "react";
import { connect } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";

import { connectToApi, ApiConnectedOwnProps } from "@api/components/connectToApi";
import { makeHeadlineStatsGet } from "@api/index";
import { equalDateAndMoment } from "@app/modules/util";
import { compose } from "@util/compose";
import {
    MusicIdProps,
    getHeadlineStatsSetForProps,
    getHeadlineStatsLastLoadedForProps,
    getTotalStat,
    getPlaylistsStat,
    getNewReleasesStat,
    getTrackedPlaylistsStat,
    getPercentageOfAllStat,
    getPercentagePlaylistsStat,
    getPercentageNewReleasesStat,
    getPercentageTrackedPlaylistsStat,
    getSettingTo,
    getSettingFrom,
    getTotalCatalogueStat,
} from "../../selectors";
import { messages } from "../../messages";
import { SalesState } from "../../models";
import { HeadlineStat } from "../HeadlineStat";

interface StateProps extends ApiConnectedOwnProps {
    percentageOfAllStat?: number;
    percentagePlaylistsStat?: number;
    percentageNewReleasesStat?: number;
    percentageTrackedPlaylistsStat?: number;
    totalStat?: number;
    totalCatalogueStat?: number;
    playlistsStat?: number;
    newReleasesStat?: number;
    trackedPlaylistsStat?: number;
}

type Props = StateProps & MusicIdProps;

const HeadlineStats = ({
    percentageOfAllStat,
    percentagePlaylistsStat,
    percentageNewReleasesStat,
    percentageTrackedPlaylistsStat,
    totalStat,
    totalCatalogueStat,
    playlistsStat,
    newReleasesStat,
    trackedPlaylistsStat,
}: Props) => {
    const intl = useIntl();
    return(
        <div className="headline-stats">
            {percentageNewReleasesStat != null &&
                <HeadlineStat
                    label={<FormattedMessage {...messages.headlineStatNewReleasesLabel} />}
                    value={newReleasesStat}
                    totalCount={totalStat}
                    title={intl.formatMessage(messages.numberStreams, {
                        number: intl.formatNumber(newReleasesStat),
                        total: newReleasesStat,
                    })}
                    isPercentage
                />
            }
            {percentageOfAllStat != null &&
                <HeadlineStat
                    label={<FormattedMessage {...messages.headlineStatWholeCatalogueLabel} />}
                    value={totalStat}
                    totalCount={totalCatalogueStat}
                    title={intl.formatMessage(messages.numberStreams, {
                        number: intl.formatNumber(totalStat),
                        total: totalStat,
                    })}
                    isPercentage
                />
            }
            {percentagePlaylistsStat != null &&
                <HeadlineStat
                    label={<FormattedMessage {...messages.headlineStatPlaylistsLabel} />}
                    value={playlistsStat}
                    totalCount={totalStat}
                    title={intl.formatMessage(messages.numberStreams, {
                        number: intl.formatNumber(playlistsStat),
                        total: playlistsStat,
                    })}
                    isPercentage
                />
            }
            {percentageTrackedPlaylistsStat != null &&
                <HeadlineStat
                    label={<FormattedMessage {...messages.headlineStatTrackedPlaylistsLabel} />}
                    value={trackedPlaylistsStat}
                    totalCount={totalStat}
                    title={intl.formatMessage(messages.numberStreams, {
                        number: intl.formatNumber(trackedPlaylistsStat),
                        total: trackedPlaylistsStat,
                    })}
                    isPercentage
                />
            }
        </div>
    );
};

const mapStateToProps = (state: SalesState, props: MusicIdProps) => {
    const from = getSettingFrom(state);
    const to = getSettingTo(state);
    const actionCreators = [ makeHeadlineStatsGet(props.albumId, props.trackId, props.artistId, from, to) ];
    const requestId = [ from.format() + "_" + to.format() ];
    const set = getHeadlineStatsSetForProps(state, props);
    let lastLoaded = getHeadlineStatsLastLoadedForProps(state, props);
    // If the settings dates are different to the loaded set, load again
    if (lastLoaded != null && set != null &&
        (!equalDateAndMoment(set.from, from) || !equalDateAndMoment(set.to, to))
    ) {
        lastLoaded = null;
    }

    return {
        percentageOfAllStat: getPercentageOfAllStat(state, props),
        percentagePlaylistsStat: getPercentagePlaylistsStat(state, props),
        percentageNewReleasesStat: getPercentageNewReleasesStat(state, props),
        percentageTrackedPlaylistsStat: getPercentageTrackedPlaylistsStat(state, props),
        totalStat: getTotalStat(state, props),
        totalCatalogueStat: getTotalCatalogueStat(state, props),
        playlistsStat: getPlaylistsStat(state, props),
        newReleasesStat: getNewReleasesStat(state, props),
        trackedPlaylistsStat: getTrackedPlaylistsStat(state, props),
        // API options
        actionCreators,
        requestId,
        lastLoaded: [ lastLoaded ],
    } as StateProps;
};

const ConnectedHeadlineStats = compose<Props, MusicIdProps>(
    connect(mapStateToProps, null),
    connectToApi({ showRefresh: false }),
)(HeadlineStats);

export {
    Props as HeadlineStatsProps,
    ConnectedHeadlineStats as HeadlineStats,
    HeadlineStats as HeadlineStatsPresenter,
};
